import { useEffect, useRef, useState } from "react";
import { Archive, Check, Download, LoaderCircle, Plus, RefreshCw, Trash2 } from "lucide-react";
import "./TenantRefresh.css";

const API = (process.env.REACT_APP_TENANT_REFRESH_API_BASE || "").replace(/\/$/, "");
const RUNNING = new Set(["QUEUED", "DISCOVERING", "RESOLVING", "QUEUED_EXPORT", "EXPORTING", "PACKAGING"]);
const CLASSES = ["Exported Accounts", "Actor", "IdentityActor", "ActorRole", "ActorContext", "ActorOrgUnit", "PfiUserTask", "Agent"];
const TENANTS = [...new Set((process.env.REACT_APP_TENANT_REFRESH_TENANTS || "AX5").split(",")
  .map(tenant => tenant.trim().toUpperCase()).filter(tenant => ["AX5", "PP1", "AX2"].includes(tenant)))];
const DEFAULT_TENANT = TENANTS.includes("AX5") ? "AX5" : TENANTS[0] || "AX5";
const STEP_GUIDE = [
  { id: "connect", label: "Connect and validate", detail: "Load PRD and selected-tenant credentials from Secrets Manager, authenticate with read-only IFS and Landmark scopes, and validate the IFS catalogs." },
  { id: "membership", label: "Discover eligible accounts", detail: "Read the PRD non-production-access role and every distribution group in the selected tenant. Follow membership pages and reconcile totals." },
  { id: "resolve", label: "Resolve target identities", detail: "Hydrate source GUIDs in bounded batches. Match PRD users by exact username, then exact ActorId or FederationId. Ambiguous identities, invalid Actor IDs, and unmatched accounts remain review issues. Run-specific exclusions require a reason." },
  { id: "review", label: "Review the resolved scope", detail: "Download and inspect the resolved-account workbook. No selected-tenant backup export begins until an operator explicitly approves the scope." },
  { id: "ifs", label: "Export selected-tenant IFS accounts", detail: "Re-read every selected account from that tenant. The export fails if any approved account GUID no longer resolves." },
  { id: "classes", label: "Export actor-owned Landmark data", detail: "Export Actor, IdentityActor, ActorRole, ActorContext, top-level ActorOrgUnit, and PfiUserTask. Follow every next link, require exact owner equality, and retain per-actor page and row coverage." },
  { id: "agents", label: "Scan and validate Agents", detail: "Agent lacks a reliable actor filter, so scan its full class in lightweight pages. Checkpoint the scan and fetch each match by GUID with exact owner validation." },
  { id: "package", label: "Validate and package backup", detail: "Reconcile saved chunks, reject duplicate rows and Excel truncation, add coverage and validation sheets, encrypt the workbook in private S3, and issue a short-lived download." },
];
const FILE_GUIDE = [
  ["frontend/src/TenantRefresh.jsx", "Run history, tenant selection, exclusions, step states, scope approval, progress, and downloads."],
  ["frontend/src/TenantRefresh.css", "Responsive feature styling in the existing DEV site theme."],
  ["frontend/src/App.jsx", "DEV-only route and navigation plus visitor-group denial."],
  ["frontend/src/HomePage.jsx", "Tenant Refresh tile in the existing functionality area."],
  ["Lambdas/lambda_tenant_refresh.py", "Authorized API for runs, status, scope approval, and owner-scoped downloads."],
  ["Lambdas/tenant_refresh_infor.py", "Read-only Infor OAuth, IFS discovery and identity lookup, and Landmark pagination."],
  ["Lambdas/lambda_tenant_refresh_worker.py", "Durable checkpoints, identity resolution, exact-owner exports, coverage audit, and workbook packaging."],
  ["infrastructure/tenant-refresh-dev.yaml", "Isolated DEV resources: private S3, DynamoDB, authenticated API, Lambda workers, and Standard Step Functions."],
  [".github/workflows/deploy-dev.yml", "Existing DEV deployment workflow, including the optional Tenant Refresh stack."],
  ["Lambdas/tests/test_tenant_refresh.py", "API authorization, tenant, idempotency, ownership, and download tests."],
  ["Lambdas/tests/test_tenant_refresh_worker.py", "Tenant isolation, IFS/Landmark pagination, checkpoints, identity scope, and workbook safety tests."],
  ["docs/features/tenant-refresh.md", "End-to-end process, configuration, security, and feature-file inventory."],
];

async function request(path, options = {}) {
  if (!API) throw new Error("Tenant Refresh DEV backend has not been configured.");
  const token = window.__VP_AUTH_TOKEN;
  if (!token) throw new Error("Sign in to access Tenant Refresh.");
  const response = await fetch(`${API}/tenant-refresh${path}`, {
    ...options, cache: "no-store",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `Tenant Refresh request failed (${response.status}).`);
  return payload;
}

const displayStatus = (status = "") => status.toLowerCase().replace(/_/g, " ");
const formatDate = value => value ? new Date(value).toLocaleString() : "";

export default function TenantRefresh() {
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [label, setLabel] = useState("");
  const [tenant, setTenant] = useState(DEFAULT_TENANT);
  const [exclusions, setExclusions] = useState([]);
  const [reviewed, setReviewed] = useState(false);
  const [startPending, setStartPending] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteDialog = useRef(null);
  const pendingStart = useRef(null);
  const mounted = useRef(true);
  const run = runs.find(entry => entry.runId === selectedId);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer;
    async function sync() {
      try {
        const payload = await request("/runs", { signal: controller.signal });
        if (controller.signal.aborted) return;
        setRuns(payload.runs || []);
        setSelectedId(current => payload.runs?.some(entry => entry.runId === current) ? current : payload.runs?.[0]?.runId || "");
        setError("");
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          timer = setTimeout(sync, 8000);
        }
      }
    }
    sync();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [revision]);

  useEffect(() => { setReviewed(false); }, [selectedId]);

  useEffect(() => {
    if (deleteTarget && !deleteDialog.current.open) deleteDialog.current.showModal();
    if (!deleteTarget && deleteDialog.current.open) deleteDialog.current.close();
  }, [deleteTarget]);

  async function perform(name, action) {
    setBusy(name);
    setError("");
    try {
      await action();
    } catch (failure) {
      if (mounted.current) setError(failure.message);
    } finally {
      if (mounted.current) setBusy("");
    }
  }

  function start(event) {
    event.preventDefault();
    perform("start", async () => {
      if (!pendingStart.current) {
        pendingStart.current = { requestId: crypto.randomUUID().replace(/-/g, ""), tenant, label: label.trim(), exclusions };
        setStartPending(true);
      }
      const created = await request("/runs", { method: "POST", body: JSON.stringify(pendingStart.current) });
      pendingStart.current = null;
      if (!mounted.current) return;
      setStartPending(false);
      setLabel("");
      setTenant(DEFAULT_TENANT);
      setExclusions([]);
      setRuns(current => [created, ...current.filter(entry => entry.runId !== created.runId)]);
      setSelectedId(created.runId);
      setRevision(current => current + 1);
    });
  }

  function download(action) {
    perform(action, async () => {
      const payload = await request(`/runs/${run.runId}/${action}`);
      if (!mounted.current) return;
      const anchor = document.createElement("a");
      anchor.href = payload.url;
      anchor.rel = "noopener noreferrer";
      anchor.referrerPolicy = "no-referrer";
      anchor.click();
    });
  }

  function approve() {
    perform("approve", async () => {
      await request(`/runs/${run.runId}/export`, { method: "POST", body: JSON.stringify({ approved: true }) });
      if (!mounted.current) return;
      setRuns(current => current.map(entry => entry.runId === run.runId ? { ...entry, status: "QUEUED_EXPORT", stage: "Export queued" } : entry));
      setReviewed(false);
      setRevision(current => current + 1);
    });
  }

  function editExclusion(index, field, value) {
    setExclusions(current => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, [field]: value } : entry));
  }

  function deleteRun() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    perform("delete", async () => {
      await request(`/runs/${target.runId}`, { method: "DELETE", body: JSON.stringify({ confirmed: true }) });
      if (!mounted.current) return;
      setRuns(current => current.map(entry => entry.runId === target.runId
        ? { ...entry, status: "DELETING", stage: "Stopping run and permanently deleting files", error: "" } : entry));
      setDeleteTarget(null);
      setRevision(current => current + 1);
    });
  }

  const summary = run?.summary || {};
  const progress = run?.progress || {};
  const rows = progress.classRows || summary.classRows || {};
  const active = RUNNING.has(run?.status);

  return (
    <section className="tenant-refresh" aria-labelledby="refresh-title">
      <header className="cug-header">
        <div className="cug-header-top"><h1 className="cug-title tr-title" id="refresh-title">Tenant Refresh</h1></div>
        <div className="cug-subtitle">Infor / {TENANTS.join(" / ")} / DEV</div>
      </header>
      <div className="tr-body">
      {error && <div className="tr-error" role="alert">{error}</div>}
      <form className="tr-create" onSubmit={start}>
        <h2>New Backup</h2>
        <div className="tr-create-row">
          <label>Run label<input required maxLength={100} value={label} onChange={event => setLabel(event.target.value)} disabled={!!busy || startPending} /></label>
          <label className="tr-tenant">Tenant<select value={tenant} onChange={event => setTenant(event.target.value)} disabled={!!busy || startPending}>
            {TENANTS.map(value => <option key={value} value={value}>{value}</option>)}
          </select></label>
          <button className="tr-primary" type="submit" disabled={!!busy || !API || (!startPending && !label.trim())}>
            {busy === "start" ? <LoaderCircle size={17} className="tr-spinning" /> : <Plus size={17} />}
            {startPending ? "Retry start" : "Start discovery"}
          </button>
        </div>
        <div className="tr-exclusion-heading"><h3>Run-specific Exclusions <span>({exclusions.length})</span></h3>
          <button type="button" className="tr-icon" title="Add exclusion" aria-label="Add exclusion" disabled={!!busy || startPending || exclusions.length >= 100}
            onClick={() => setExclusions(current => [...current, { userName: "", reason: "" }])}><Plus size={17} /></button>
        </div>
        {exclusions.map((entry, index) => <div className="tr-exclusion" key={index}>
          <label>Username<input required maxLength={254} value={entry.userName} disabled={!!busy || startPending} onChange={event => editExclusion(index, "userName", event.target.value)} /></label>
          <label>Reason<input required maxLength={250} value={entry.reason} disabled={!!busy || startPending} onChange={event => editExclusion(index, "reason", event.target.value)} /></label>
          <button type="button" className="tr-icon" title="Remove exclusion" aria-label={`Remove exclusion ${index + 1}`} disabled={!!busy || startPending}
            onClick={() => setExclusions(current => current.filter((_, entryIndex) => entryIndex !== index))}><Trash2 size={16} /></button>
        </div>)}
      </form>
      <div className="tr-workspace">
        <aside className="tr-history" aria-label="Recent backup runs">
          <div className="tr-section-heading"><h2>Recent Runs</h2><button type="button" className="tr-icon" title="Refresh run history" aria-label="Refresh run history" onClick={() => setRevision(current => current + 1)}><RefreshCw size={16} /></button></div>
          {loading && <p className="tr-muted" role="status">Loading runs...</p>}
          {!loading && !runs.length && <p className="tr-muted">No backup runs.</p>}
          {runs.map(entry => <div className="tr-run-row" key={entry.runId}>
            <button type="button" className={`tr-run ${selectedId === entry.runId ? "tr-selected" : ""}`}
            aria-pressed={selectedId === entry.runId} onClick={() => setSelectedId(entry.runId)}>
            <strong>{entry.label}</strong><span className={`tr-status tr-status-${entry.status}`}>{displayStatus(entry.status)}</span><time dateTime={entry.createdAt}>{formatDate(entry.createdAt)}</time>
            </button>
            <button type="button" className="tr-icon tr-delete" disabled={!!busy || entry.status === "DELETING"}
              title={entry.status === "DELETING" ? "Permanently deleting run" : "Permanently delete run and files"}
              aria-label={`Permanently delete ${entry.label}`} onClick={() => { setError(""); setDeleteTarget(entry); }}>
              {entry.status === "DELETING" ? <LoaderCircle size={16} className="tr-spinning" /> : <Trash2 size={16} />}
            </button>
          </div>)}
        </aside>
        <div className="tr-detail" aria-live="polite" aria-atomic="false">
          {!run ? <div className="tr-empty"><Archive size={28} /><h2>No Run Selected</h2></div> : <>
            <div className="tr-section-heading"><h2>{run.label}</h2><span className={`tr-status tr-status-${run.status}`}>{displayStatus(run.status)}</span></div>
            <div className="tr-stage">{active && <LoaderCircle size={16} className="tr-spinning" />}<strong>{run.stage}</strong><span>Updated {formatDate(run.updatedAt)}</span></div>
            {run.status === "DELETING" && <div className="tr-warning" role="status">Stopping background work and permanently deleting this run's stored files. This can take several minutes.</div>}
            <section className="tr-step-panel" aria-labelledby="tr-steps-title">
              <div className="tr-section-heading"><h3 id="tr-steps-title">Workflow Steps</h3><span className="tr-muted">{run.tenant} / backup only</span></div>
              <ol className="tr-steps">{STEP_GUIDE.map((step, index) => {
                const state = run.steps?.find(entry => entry.id === step.id)?.status || (index === 0 && active ? "in_progress" : "pending");
                return <li className={`tr-step tr-step-${state}`} key={step.id}>
                  <span className="tr-step-number">{state === "completed" ? <Check size={15} /> : state === "in_progress" ? <LoaderCircle size={15} className="tr-spinning" /> : index + 1}</span>
                  <div><strong>{step.label}</strong><span>{state.replace(/_/g, " ")}</span><p>{step.detail}</p></div>
                </li>;
              })}</ol>
              {progress.current && <p className="tr-current-step" role="status">Current work: {progress.current}</p>}
            </section>
            <dl className="tr-counts">
              <div><dt>Resolved {run.tenant} accounts</dt><dd>{summary.resolvedAccounts ?? "-"}</dd></div>
              <div><dt>Unresolved / review</dt><dd>{summary.unresolved ?? "-"}</dd></div>
              <div><dt>Excluded</dt><dd>{summary.excluded ?? run.exclusions?.length ?? 0}</dd></div>
            </dl>
            {!!progress.memberships && <dl className="tr-progress"><div><dt>Source memberships</dt><dd>{progress.memberships}</dd></div><div><dt>Accounts read</dt><dd>{progress.accountsRead}</dd></div><div><dt>Catalog scanned</dt><dd>{progress.scanned}</dd></div></dl>}
            {run.status === "REVIEW_READY" && <div className="tr-review">
              <div className="tr-actions"><button type="button" onClick={() => download("scope")} disabled={!!busy}><Download size={16} /> Download resolved scope</button></div>
              <label className="tr-review-check"><input type="checkbox" checked={reviewed} onChange={event => setReviewed(event.target.checked)} />I reviewed the scope and accept its exclusions and unresolved entries.</label>
              <button type="button" className="tr-primary" onClick={approve} disabled={!!busy || !reviewed || !summary.resolvedAccounts}><Check size={16} />Approve &amp; export {run.tenant} backup</button>
            </div>}
            {(active || run.status === "COMPLETED") && <>
              <div className="tr-section-heading"><h3>Exported Rows</h3>{progress.agentPages > 0 && <span className="tr-muted">Agent pages scanned: {progress.agentPages.toLocaleString()}</span>}</div>
              <table className="tr-table"><thead><tr><th>Data</th><th>Rows</th></tr></thead><tbody>{CLASSES.map(name => <tr key={name}><td>{name}</td><td>{rows[name] == null ? "-" : rows[name].toLocaleString()}</td></tr>)}</tbody></table>
            </>}
            {run.status === "COMPLETED" && <div className="tr-download">
              {summary.requiresReview && <div className="tr-warning" role="status">Review required: {summary.unresolved || 0} scope issues; {summary.coverageWarnings || 0} Actor/class queries returned no records.</div>}
              <div className="tr-actions"><button type="button" className="tr-primary" disabled={!!busy} onClick={() => download("download")}><Download size={17} />Download backup workbook</button>
                <button type="button" disabled={!!busy} onClick={() => download("scope")}><Download size={16} />Resolved scope</button></div>
              {summary.sha256 && <div className="tr-checksum"><span>SHA-256</span><code>{summary.sha256}</code></div>}
            </div>}
            {["FAILED", "DELETE_FAILED"].includes(run.status) && <div className="tr-error" role="alert">{run.error || "Backup failed. Operator review is required."}</div>}
            <details className="tr-file-guide"><summary>Workflow and file documentation</summary>
              <p>Runs continue in AWS Step Functions and Lambda if this page is closed. Reopening Tenant Refresh reloads saved status. Export starts only after the account scope is reviewed and approved.</p>
              <ul>{FILE_GUIDE.map(([path, purpose]) => <li key={path}><code>{path}</code><span>{purpose}</span></li>)}</ul>
            </details>
          </>}
        </div>
      </div>
      </div>
      <dialog ref={deleteDialog} className="tr-delete-dialog" aria-labelledby="tr-delete-title" aria-describedby="tr-delete-description"
        onCancel={event => { if (busy === "delete") event.preventDefault(); else setDeleteTarget(null); }}>
        <h2 id="tr-delete-title">Permanently delete run?</h2>
        <p id="tr-delete-description">Delete <strong>{deleteTarget?.label}</strong> from Recent Runs and permanently erase its stored backup workbooks, checkpoints, and file versions. Any active background work will be stopped. This cannot be undone.</p>
        {error && <div className="tr-error" role="alert">{error}</div>}
        <div className="tr-actions">
          <button type="button" autoFocus disabled={busy === "delete"} onClick={() => setDeleteTarget(null)}>Cancel</button>
          <button type="button" className="tr-danger" disabled={!!busy || !deleteTarget} onClick={deleteRun}>
            {busy === "delete" ? <LoaderCircle size={16} className="tr-spinning" /> : <Trash2 size={16} />}Delete permanently
          </button>
        </div>
      </dialog>
    </section>
  );
}